import React, { useMemo } from 'react';
import { useRouteMatch, useLocation } from 'react-router-dom';
import ModuleSidebarLayout from './ModuleSidebarLayout';
import { NAV_MODULES, resolveModuleKey, filterNavForRole } from '../navigation';
import usePharmacyBadgeCounts from '../hooks/usePharmacyBadgeCounts';
import useInvRole from '../hooks/useInvRole';
import { ActiveStoreProvider } from '../hooks/useActiveStore';
import InvStoreSwitcher from './InvStoreSwitcher';

/**
 * Picks the workspace the current URL belongs to and hands its nav config to the
 * sidebar shell. Kept as one layout rather than three route-nested ones so crossing
 * a module boundary swaps only the content, not the whole shell.
 *
 * Pharmacy is the only module with badge providers today; the hook's `enabled` gate
 * means nothing is fetched while the user is elsewhere. There must be exactly one
 * caller of it.
 */
export default function InvLayout({ children }) {
  const { path: suiteBase } = useRouteMatch();
  const { pathname } = useLocation();
  const moduleKey = resolveModuleKey(pathname, suiteBase);
  const badges = usePharmacyBadgeCounts(moduleKey === 'pharmacy');
  const { can, modules } = useInvRole();
  const navConfig = useMemo(
    () => filterNavForRole(NAV_MODULES[moduleKey], can, modules),
    [moduleKey, can, modules],
  );

  return (
    <ActiveStoreProvider>
      <ModuleSidebarLayout
        navConfig={navConfig}
        suiteBase={suiteBase}
        badges={badges}
        sidebarExtra={<InvStoreSwitcher variant="sidebar" />}
        topBarExtra={<InvStoreSwitcher variant="topbar" />}
      >
        {children}
      </ModuleSidebarLayout>
    </ActiveStoreProvider>
  );
}
